import {
  Col,
  Flex,
  Form,
  FormRule,
  Grid,
  Input,
  message,
  Radio,
  Row,
  Select,
  Typography,
} from 'antd';
import { stickyTop } from '@/components/layout/MobileHeader/stickyTop';
import React, { FC, Fragment, useEffect, useState, useRef } from 'react';
import { FloatingLabel } from 'src/components/ui/FloatingLabel';
import { Hint } from '../assets/Hint';
import { CustomButton } from 'src/components/ui';
import { Header } from '../assets/Header';
import { updateProfile } from '@/api/user';
import { useMutation } from '@tanstack/react-query';
import { BackButton } from '@/components/shared';
import { ROUTES } from '@/types/routes';
import { useNavigate } from 'react-router';
import queryClient from '@/client/queryClient';
import { Profile } from '@/pages/my-profile/general';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectProfileCompleteness, selectUser } from '@/slice/user';
import { CustomTextarea } from '@/components/ui/CustomTextarea';
import Blocker from '@/components/blocker';
import { ProfileProgressBar } from '@/components/shared/profile-progress-bar';
import './index.scss';
import { formatNumberWithCommas } from '@/helpers/formatNumberWithCommas';
import { validatePercentageValue } from './validatePercentage';

interface FormItem {
  name: string;
  label: string;
  type: 'input' | 'textarea' | 'yes-no';
  rules?: FormRule[];
  format?: 'currency' | 'percentage' | 'integer' | 'number';
}

// TCX-866: royalty/adFee are the `{ type, value, note }` object. This unwraps
// it to a plain display value — no type selector or note field yet, that's
// TCX-867. Reused for both the form hydration and the change-diff below so
// they agree on what "the current value" means for these two fields.
function feeDisplayValue(raw: unknown): unknown {
  if (raw !== null && typeof raw === 'object') {
    return (raw as { value?: unknown }).value;
  }
  return raw;
}

// TCX-866: royalty/adFee are always `percent` on this profile — there is no
// type picker in this editor yet (TCX-867/868 scope). The backend now
// requires a type whenever a fee has a value (a value-only fee object can't
// be formatted reliably), so a save from this editor must always send one
// alongside `.value`, matching the migration's own default for these two
// fields.
const LEGACY_ROYALTY_AD_FEE_TYPE = 'percent';

type Categories = 'investment';

const parseFormattedNumber = (value: string): string => {
  if (!value && value !== '0') return '';
  return value.toString().replace(/,/g, '');
};

// ---------------------------------------------
// CurrencyInput Component
// ---------------------------------------------
const CurrencyInput: FC<{
  value?: string;
  onChange?: (value: string) => void;
  size?: 'large';
}> = ({ value, onChange, size = 'large' }) => {
  const [displayValue, setDisplayValue] = useState<string>('');
  const inputRef = useRef<any>(null);

  useEffect(() => {
    if (value !== undefined && value !== null) {
      setDisplayValue(formatNumberWithCommas(value));
    } else {
      setDisplayValue('');
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const cursorPosition = e.target.selectionStart || 0;

    const raw = parseFormattedNumber(inputValue);

    const formatted = formatNumberWithCommas(raw);

    // Calculate new cursor position after formatting
    const beforeCursor = inputValue.slice(0, cursorPosition);
    const digitsBeforeCursor = beforeCursor.replace(/,/g, '').length;

    // Count characters in formatted string up to the same digit position
    let newCursorPos = 0;
    let digitCount = 0;

    for (
      let i = 0;
      i < formatted.length && digitCount < digitsBeforeCursor;
      i++
    ) {
      if (formatted[i] !== ',') {
        digitCount++;
      }
      newCursorPos = i + 1;
    }

    setDisplayValue(formatted);

    if (onChange) {
      onChange(raw);
    }

    // Set cursor position after state update
    setTimeout(() => {
      if (inputRef.current?.input) {
        inputRef.current.input.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  return (
    <Input
      ref={inputRef}
      size={size}
      value={displayValue}
      onChange={handleChange}
      prefix="$"
    />
  );
};

const PercentageInput: FC<{
  value?: string;
  onChange?: (value: string) => void;
  size?: 'large';
}> = ({ value, onChange, size = 'large' }) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (onChange) {
      onChange(inputValue);
    }
  };

  return (
    <Input size={size} value={value || ''} onChange={handleChange} suffix="%" />
  );
};

const NumberInput: FC<{
  value?: string;
  onChange?: (value: string) => void;
  size?: 'large';
}> = ({ value, onChange, size = 'large' }) => {
  const [displayValue, setDisplayValue] = useState<string>('');

  useEffect(() => {
    if (value !== undefined && value !== null) {
      setDisplayValue(formatNumberWithCommas(value));
    } else {
      setDisplayValue('');
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    const raw = parseFormattedNumber(inputValue);

    setDisplayValue(formatNumberWithCommas(raw));

    if (onChange) {
      onChange(raw);
    }
  };

  return <Input size={size} value={displayValue} onChange={handleChange} />;
};

const IntegerInput: FC<{
  value?: string;
  onChange?: (value: string) => void;
  size?: 'large';
}> = ({ value, onChange, size = 'large' }) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (onChange) {
      onChange(inputValue);
    }
  };

  return <Input size={size} value={value || ''} onChange={handleChange} />;
};

const CashRequiredInput: FC<{
  value?: number | string;
  onChange?: (value: string | undefined) => void;
  size?: 'large';
}> = ({ value, onChange, size = 'large' }) => {
  const [displayValue, setDisplayValue] = useState<string>('');

  useEffect(() => {
    if (value !== undefined && value !== null) {
      setDisplayValue(formatNumberWithCommas(value.toString()));
    } else {
      setDisplayValue('');
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    const raw = parseFormattedNumber(inputValue);

    setDisplayValue(formatNumberWithCommas(raw));

    if (onChange) {
      onChange(raw || undefined);
    }
  };

  return (
    <Input
      size={size}
      value={displayValue}
      onChange={handleChange}
      prefix="$"
    />
  );
};

// ---------------------------------------------
// Component
// ---------------------------------------------
export const FinancialInformation: FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const user = useAppSelector(selectUser);
  const profileCompleteness = useAppSelector(selectProfileCompleteness);

  const [formChanged, setFormChanged] = useState(false);
  const [cashRequiredRange, setCashRequiredRange] = useState<
    'range' | 'less' | 'more'
  >('range');

  // Store values to preserve them during mode switches
  const [storedMin, setStoredMin] = useState<number | undefined>();
  const [storedMax, setStoredMax] = useState<number | undefined>();

  const { lg } = Grid.useBreakpoint();

  // ---------------------------------------------
  // Initialize form with user data (raw values)
  // ---------------------------------------------
  useEffect(() => {
    // TCX-866: royalty/adFee may be the new object or the legacy scalar —
    // feeDisplayValue shows the plain value either way. TCX-867 still owns
    // surfacing `type`/`note` once that editor exists.
    form.setFieldsValue({
      ...user,
      royalty: feeDisplayValue(user.royalty),
      adFee: feeDisplayValue(user.adFee),
    });

    // Store the initial min/max values
    if (user.cashRequiredMin !== undefined) {
      setStoredMin(user.cashRequiredMin);
    }
    if (user.cashRequiredMax !== undefined) {
      setStoredMax(user.cashRequiredMax);
    }
  }, [form, user]);

  useEffect(() => {
    if (user.cashRequiredMin && user.cashRequiredMax) {
      setCashRequiredRange('range');
    } else if (user.cashRequiredMin && !user.cashRequiredMax) {
      setCashRequiredRange('more');
    } else if (!user.cashRequiredMin && user.cashRequiredMax) {
      setCashRequiredRange('less');
    }
  }, [user.cashRequiredMin, user.cashRequiredMax]);

  // ---------------------------------------------
  // Auto-clear/restore fields based on range selection
  // ---------------------------------------------
  useEffect(() => {
    const currentMin = form.getFieldValue('cashRequiredMin');
    const currentMax = form.getFieldValue('cashRequiredMax');

    // Update stored values when form changes
    if (currentMin !== undefined) setStoredMin(currentMin);
    if (currentMax !== undefined) setStoredMax(currentMax);

    if (cashRequiredRange === 'range') {
      // Restore both values from stored state
      form.setFieldsValue({
        cashRequiredMin: currentMin ?? storedMin,
        cashRequiredMax: currentMax ?? storedMax,
      });
    } else if (cashRequiredRange === 'less') {
      // Keep/restore max, clear min
      form.setFieldsValue({
        cashRequiredMax: currentMax ?? storedMax,
        cashRequiredMin: undefined,
      });
    } else if (cashRequiredRange === 'more') {
      // Keep/restore min, clear max
      form.setFieldsValue({
        cashRequiredMin: currentMin ?? storedMin,
        cashRequiredMax: undefined,
      });
    }
  }, [cashRequiredRange]);

  // ---------------------------------------------
  // Validation Rules
  // ---------------------------------------------
  const currencyRules: FormRule[] = [
    {
      validator: (_, value) => {
        if (!value) return Promise.resolve();

        // Value is already raw (no commas) thanks to CurrencyInput
        if (!/^\d*\.?\d*$/.test(String(value))) {
          return Promise.reject(
            new Error('Please enter a valid currency amount'),
          );
        }

        const numValue = parseFloat(String(value));
        if (numValue < 0) {
          return Promise.reject(new Error('Amount must be positive'));
        }

        return Promise.resolve();
      },
    },
  ];

  // TCX-866: royalty/adFee hydrate as plain strings via feeDisplayValue above,
  // so this validator's String(value) sees the real number, not "[object
  // Object]". The rule is per-field because a migrated fee can hold a preserved
  // non-numeric legacy value (e.g. '$475/month' typed as percent) — passing the
  // hydrated original lets validatePercentageValue accept it UNCHANGED, so the
  // franchisor isn't blocked from saving other fields, while a NEW edit still
  // must be numeric. TCX-867 still owns validating a chosen `type`.
  const percentageRulesFor = (originalValue: unknown): FormRule[] => [
    {
      validator: (_, value) => {
        const error = validatePercentageValue(value, originalValue);
        return error
          ? Promise.reject(new Error(error))
          : Promise.resolve();
      },
    },
  ];

  const integerRules: FormRule[] = [
    {
      validator: (_, value) => {
        if (!value) return Promise.resolve();

        if (!/^\d*$/.test(String(value))) {
          return Promise.reject(new Error('Please enter a valid integer'));
        }

        const numValue = parseInt(String(value), 10);
        if (numValue < 0) {
          return Promise.reject(new Error('Value must be positive'));
        }

        return Promise.resolve();
      },
    },
  ];

  const cashRequiredRules: FormRule[] = [
    {
      validator: (_, value) => {
        if (!value && value !== 0) return Promise.resolve();

        // CashRequiredInput returns numbers, but handle both cases for safety
        let numValue: number;
        if (typeof value === 'string') {
          const cleanValue = value.replace(/,/g, '');
          if (!/^\d*\.?\d*$/.test(cleanValue)) {
            return Promise.reject(
              new Error('Please enter a valid currency amount'),
            );
          }
          numValue = parseFloat(cleanValue);
        } else if (typeof value === 'number') {
          numValue = value;
        } else {
          return Promise.reject(
            new Error('Please enter a valid currency amount'),
          );
        }

        if (isNaN(numValue) || numValue < 0) {
          return Promise.reject(new Error('Amount must be positive'));
        }

        return Promise.resolve();
      },
    },
  ];

  // ---------------------------------------------
  // Input Renderer
  // ---------------------------------------------
  const renderFormItem = (item: FormItem) => {
    if (item.type === 'input' && item.format === 'currency') {
      return <CurrencyInput />;
    }

    if (item.type === 'input' && item.format === 'percentage') {
      return <PercentageInput />;
    }

    if (item.type === 'input' && item.format === 'integer') {
      return <IntegerInput />;
    }

    if (item.type === 'input' && item.format === 'number') {
      return <NumberInput />;
    }

    if (item.type === 'input') {
      return <Input size="large" />;
    }

    if (item.type === 'textarea') {
      return <CustomTextarea />;
    }

    if (item.type === 'yes-no') {
      return (
        <Radio.Group
          style={{ marginTop: 24, marginLeft: 24 }}
          options={[
            { value: true, label: 'Yes' },
            { value: false, label: 'No' },
          ]}
        />
      );
    }

    return null;
  };

  // ---------------------------------------------
  // Investment field config
  // ---------------------------------------------
  const formFields: Record<Categories, FormItem[]> = {
    investment: [
      {
        name: 'franchiseFee',
        label: 'Franchise Fee (USD)',
        type: 'input',
        format: 'currency',
        rules: currencyRules,
      },
      {
        name: 'minNetWorth',
        label: 'Minimum Net Worth (USD)',
        type: 'input',
        format: 'currency',
        rules: currencyRules,
      },
      {
        // TCX-866: royalty/adFee are { type, value, note } on the backend; this
        // form models both as a plain percentage <Input> showing just the value
        // (see feeDisplayValue), and onFinish submits the `.value`/`.type` keys
        // the allowlist requires. The rule is keyed off the hydrated original so
        // a preserved non-numeric legacy value saves unchanged. TCX-867 will
        // replace these with the real FeeValue editor (type selector + note).
        name: 'royalty',
        label: 'Royalty (%)',
        type: 'input',
        format: 'percentage',
        rules: percentageRulesFor(feeDisplayValue(user.royalty)),
      },
      {
        name: 'adFee',
        label: 'Advertising Fee (%)',
        type: 'input',
        format: 'percentage',
        rules: percentageRulesFor(feeDisplayValue(user.adFee)),
      },
      {
        name: 'avgNumberOfStaff',
        label: "Average Number of Staff in Franchisee's Business",
        type: 'input',
        format: 'integer',
        rules: integerRules,
      },
      {
        name: 'specialDiscountPrograms',
        label: 'Special Discount Programs',
        type: 'textarea',
      },
      {
        name: 'item19',
        label: 'Item 19',
        type: 'yes-no',
      },
      {
        name: 'visaCandidates',
        label: 'Visa Candidates',
        type: 'yes-no',
      },
      {
        name: 'semiAbsenteeModel',
        label: 'Semi-Absentee Model',
        type: 'yes-no',
      },
      {
        name: 'b2b',
        label: 'B2B',
        type: 'yes-no',
      },
      {
        name: 'homeBased',
        label: 'Home-Based',
        type: 'yes-no',
      },
      {
        name: 'masterFranchiseOpportunities',
        label: 'Master Franchise Opportunities',
        type: 'yes-no',
      },
      {
        name: 'veteranDiscount',
        label: 'Veteran Discount',
        type: 'yes-no',
      },
      {
        name: 'sbaEligible',
        label: 'SBA Eligible',
        type: 'yes-no',
      },
      {
        name: 'e2VisaFriendly',
        label: 'E2 Visa Friendly',
        type: 'yes-no',
      },
    ],
  };

  // ---------------------------------------------
  // Mutation
  // ---------------------------------------------
  const profileMutation = useMutation({
    mutationFn: updateProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      message.success('Profile updated successfully');
      setFormChanged(false);
    },
    onError: (error: any) => {
      message.error(
        error?.response?.data?.message ||
          'Failed to update profile. Please try again.',
      );
    },
  });

  // ---------------------------------------------
  // Submit Handler
  // ---------------------------------------------
  const onFinish = (values: Partial<Profile>) => {
    const data: Partial<Profile> = {};

    // Handle cashRequired based on range mode
    if (cashRequiredRange === 'less') {
      data.cashRequiredMin = 0;
      data.cashRequiredMax = values.cashRequiredMax
        ? Number(values.cashRequiredMax)
        : undefined;
    } else if (cashRequiredRange === 'more') {
      data.cashRequiredMin = values.cashRequiredMin
        ? Number(values.cashRequiredMin)
        : undefined;
      data.cashRequiredMax = 0; // Use null instead of MAX_SAFE_INTEGER
    } else {
      // range
      data.cashRequiredMin = values.cashRequiredMin
        ? Number(values.cashRequiredMin)
        : undefined;
      data.cashRequiredMax = values.cashRequiredMax
        ? Number(values.cashRequiredMax)
        : undefined;
    }

    // Other fields — only send changed values
    Object.keys(values).forEach((key) => {
      const typedKey = key as keyof Profile;

      // Skip cashRequired fields as they're already handled
      if (typedKey === 'cashRequiredMin' || typedKey === 'cashRequiredMax') {
        return;
      }

      const value = values[typedKey];

      // TCX-866: royalty/adFee on `user` are the { type, value, note }
      // object, but the form only ever holds the unwrapped display value
      // (see feeDisplayValue in the hydration above). Comparing against the
      // raw object here would make these two fields ALWAYS look "changed" —
      // a string is never === an object.
      const currentValue =
        typedKey === 'royalty' || typedKey === 'adFee'
          ? feeDisplayValue(user[typedKey])
          : user[typedKey];

      // Only include fields that have changed
      if (value !== undefined && value !== currentValue) {
        if (typedKey === 'royalty' || typedKey === 'adFee') {
          // The flat `royalty`/`adFee` key is no longer allowlisted (only
          // `royalty.value`/`royalty.type` etc. are — see
          // role-security.interceptor.ts), and the backend now rejects a
          // value with no type whenever the value has content. Send both.
          (data as Record<string, unknown>)[`${typedKey}.value`] = value;
          (data as Record<string, unknown>)[`${typedKey}.type`] =
            LEGACY_ROYALTY_AD_FEE_TYPE;
        } else {
          data[typedKey] = value as any;
        }
      }
    });

    profileMutation.mutate({ data });
  };

  // ---------------------------------------------
  // Range Input Renderer - REMOVED getValueFromEvent
  // ---------------------------------------------
  const rangeItem = (
    name: string,
    label: string,
    rules?: FormRule[],
    dependencies?: string[],
  ) => (
    <FloatingLabel childrenType="input" label={label}>
      <Form.Item
        name={name}
        label={label}
        labelCol={{ flex: 'none' }}
        wrapperCol={{ span: 24 }}
        rules={rules}
        dependencies={dependencies}
      >
        <CashRequiredInput />
      </Form.Item>
    </FloatingLabel>
  );

  const renderCashRequiredRange = () => {
    if (cashRequiredRange === 'range') {
      return (
        <Fragment>
          {rangeItem(
            'cashRequiredMin',
            'Min Amount (USD)',
            [
              ...cashRequiredRules,
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const maxVal = getFieldValue('cashRequiredMax');
                  if (!value || !maxVal) {
                    return Promise.resolve();
                  }

                  const min = Number(String(value).replace(/,/g, ''));
                  const max = Number(String(maxVal).replace(/,/g, ''));

                  if (isNaN(min) || isNaN(max)) {
                    return Promise.resolve();
                  }

                  if (min >= max) {
                    return Promise.reject(
                      new Error('Min amount must be less than max amount'),
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ],
            ['cashRequiredMax'],
          )}
          {rangeItem(
            'cashRequiredMax',
            'Max Amount (USD)',
            [
              ...cashRequiredRules,
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const minVal = getFieldValue('cashRequiredMin');
                  if (!value || !minVal) {
                    return Promise.resolve();
                  }

                  const min = Number(String(minVal).replace(/,/g, ''));
                  const max = Number(String(value).replace(/,/g, ''));

                  if (isNaN(min) || isNaN(max)) {
                    return Promise.resolve();
                  }

                  if (max <= min) {
                    return Promise.reject(
                      new Error('Max amount must be greater than min amount'),
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ],
            ['cashRequiredMin'],
          )}
        </Fragment>
      );
    }

    if (cashRequiredRange === 'less') {
      return rangeItem('cashRequiredMax', 'Amount (USD)', cashRequiredRules);
    }

    return rangeItem('cashRequiredMin', 'Amount (USD)', cashRequiredRules);
  };

  // ---------------------------------------------
  // Render Component
  // ---------------------------------------------
  return (
    <Fragment>
      <Blocker formChanged={formChanged} onFinish={onFinish} form={form} />

      <Row className="my-profile-financial">
        {!lg && (
          <Col span={24}>
            <BackButton />
          </Col>
        )}

        <Col span={24} lg={{ span: 12 }} className="header">
          <Header title="Investment Profile" />
        </Col>

        <Col
          span={24}
          lg={{ span: 12 }}
          // Desktop: floats in the sticky right column. Phones/tablets: a
          // sticky slot that reserves the bar's height under the subtitle,
          // so it never covers a section title but still follows the scroll.
          style={
            lg
              ? { position: 'sticky', top: stickyTop(24), zIndex: 998 }
              : {
                  position: 'sticky',
                  top: stickyTop(16),
                  zIndex: 998,
                  marginBottom: 24,
                }
          }
        >
          <ProfileProgressBar
            align={'top'}
            steps={profileCompleteness}
            inline={!lg}
          />
        </Col>

        <Col span={24} lg={{ span: 12 }}>
          <Form
            form={form}
            onFinish={onFinish}
            layout="vertical"
            requiredMark={false}
            onValuesChange={(changedValues) => {
              setFormChanged(true);

              // Update stored values when user changes them
              if (
                'cashRequiredMin' in changedValues &&
                changedValues.cashRequiredMin !== undefined
              ) {
                setStoredMin(changedValues.cashRequiredMin);
              }
              if (
                'cashRequiredMax' in changedValues &&
                changedValues.cashRequiredMax !== undefined
              ) {
                setStoredMax(changedValues.cashRequiredMax);
              }
            }}
            className="change-password-form"
          >
            <Typography.Title
              level={4}
              style={{ fontSize: '16px', margin: '0 0 24px' }}
            >
              Financial Information
            </Typography.Title>

            <Flex justify="space-between" style={{ marginBottom: 18 }}>
              <Typography.Title
                level={5}
                style={{
                  fontSize: '14px',
                  display: 'block',
                  fontWeight: 400,
                  color: '#49586F',
                }}
              >
                Cash Required
              </Typography.Title>

              <Select
                style={{ width: 100 }}
                placeholder="Select"
                onChange={(e) => setCashRequiredRange(e as any)}
                value={cashRequiredRange}
                options={[
                  { value: 'range', label: 'Range (↔)' },
                  { value: 'less', label: 'Less (<)' },
                  { value: 'more', label: 'More (>)' },
                ]}
              />
            </Flex>

            {renderCashRequiredRange()}

            <Typography.Title
              level={5}
              style={{
                fontSize: '14px',
                display: 'block',
                fontWeight: 400,
                color: '#49586F',
                marginBottom: 18,
              }}
            >
              Other Details
            </Typography.Title>

            {formFields.investment.map((item) => (
              <FloatingLabel
                childrenType={item.type === 'textarea' ? 'textarea' : 'input'}
                label={item.label}
                key={`investment-${item.name}`}
              >
                <Form.Item
                  rules={item.rules}
                  name={item.name}
                  label={item.label}
                  labelCol={{ flex: 'none' }}
                  wrapperCol={{ span: 24 }}
                >
                  {renderFormItem(item)}
                </Form.Item>
              </FloatingLabel>
            ))}

            <Flex vertical gap={8} style={{ marginTop: 'auto' }}>
              {!lg && (
                <CustomButton
                  type="default"
                  text="CANCEL"
                  onClick={() => navigate(ROUTES.MY_PROFILE)}
                />
              )}

              <Form.Item noStyle>
                <CustomButton
                  disabled={!formChanged}
                  htmlType="submit"
                  type="primary"
                  text="SAVE CHANGES"
                />
              </Form.Item>
            </Flex>
          </Form>
        </Col>

        {lg && (
          <Col span={12}>
            <Hint
              wrapperStyle={{ marginLeft: 'auto' }}
              hint={`This section captures the key financial information related to your franchise opportunity so consultants can accurately evaluate your franchise and present it to qualified candidates.
              Please ensure the information is clear, complete, and up to date.

              Note: All fields are optional unless specifically marked as required.`}
            />
          </Col>
        )}
      </Row>
    </Fragment>
  );
};
